import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Building2, MapPin, X, Send, Trash2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

export default function Customers() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [propFor, setPropFor] = useState(null);
  const { data: customers, isLoading } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });

  const [c, setC] = useState({ name: "", email: "", phone: "" });
  const [p, setP] = useState({ label: "Main Site", address: "", access_notes: "" });

  const createCustomer = async () => {
    try { await api.post("/customers", c); toast.success("Customer added"); setOpen(false); setC({ name: "", email: "", phone: "" }); qc.invalidateQueries({ queryKey: ["customers"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const createProperty = async () => {
    try { await api.post("/properties", { ...p, customer_id: propFor.id }); toast.success("Property added"); setPropFor(null); setP({ label: "Main Site", address: "", access_notes: "" }); qc.invalidateQueries({ queryKey: ["customers"] }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const invitePortal = async (cust) => {
    if (!cust.email) return toast.error("Add a customer email first");
    try { const { data } = await api.post("/portal/invite", { customer_id: cust.id, email: cust.email }); toast.success(`Portal invite sent (dev token: ${data.dev_token.slice(0, 10)}…)`); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const deleteCustomer = async (cust, force = false) => {
    if (!force && !window.confirm(`Delete ${cust.name}? This removes their properties and reminders.`)) return;
    try {
      await api.delete(`/customers/${cust.id}`, { params: force ? { force: true } : {} });
      toast.success("Customer deleted"); qc.invalidateQueries({ queryKey: ["customers"] });
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${apiErr(e.response.data.detail)}\n\nOK = delete the customer AND all their jobs/records. Cancel = keep.`)) {
          return deleteCustomer(cust, true);
        }
        return;
      }
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] p-6">
      <div className="flex items-center justify-between">
        <div><Eyebrow>Accounts</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Customers</h1></div>
        <button data-testid="new-customer-btn" onClick={() => setOpen(true)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" /> New customer</button>
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {customers.map((cust) => (
            <div key={cust.id} className="card-tac p-5" data-testid={`customer-card-${cust.id}`}>
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-cyan-500/10 text-cyan-400"><Building2 className="h-5 w-5" /></div>
                <div className="flex items-center gap-2">
                  <span className="font-mono-tac text-xs text-slate-500">{cust.property_count} site(s)</span>
                  <button data-testid={`customer-delete-${cust.id}`} onClick={() => deleteCustomer(cust)} title="Delete customer" className="rounded-md border border-slate-700 p-1.5 text-rose-300 hover:border-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <h3 className="mt-4 font-display text-base font-semibold text-white">{cust.name}</h3>
              <p className="mt-1 text-sm text-slate-400">{cust.email || "-"}</p>
              <p className="text-sm text-slate-500">{cust.phone || "-"}</p>
              <div className="mt-4 flex gap-2">
                <button data-testid={`add-property-btn-${cust.id}`} onClick={() => setPropFor(cust)} className="flex-1 rounded-lg border border-slate-700 py-2 text-xs text-white hover:border-amber-500"><MapPin className="mr-1 inline h-3 w-3" /> Property</button>
                <button data-testid={`portal-invite-btn-${cust.id}`} onClick={() => invitePortal(cust)} className="flex-1 rounded-lg border border-slate-700 py-2 text-xs text-white hover:border-cyan-500"><Send className="mr-1 inline h-3 w-3" /> Portal</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal title="New customer" onClose={() => setOpen(false)}>
          <input data-testid="customer-name-input" placeholder="Customer / company name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className={inp} />
          <input data-testid="customer-email-input" placeholder="Email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} className={inp} />
          <input data-testid="customer-phone-input" placeholder="Phone (+1…)" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} className={inp} />
          <button data-testid="customer-create-submit" disabled={!c.name} onClick={createCustomer} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Add customer</button>
        </Modal>
      )}
      {propFor && (
        <Modal title={`New property · ${propFor.name}`} onClose={() => setPropFor(null)}>
          <input data-testid="property-label-input" placeholder="Label" value={p.label} onChange={(e) => setP({ ...p, label: e.target.value })} className={inp} />
          <input data-testid="property-address-input" placeholder="Address" value={p.address} onChange={(e) => setP({ ...p, address: e.target.value })} className={inp} />
          <input data-testid="property-access-input" placeholder="Access notes" value={p.access_notes} onChange={(e) => setP({ ...p, access_notes: e.target.value })} className={inp} />
          <button data-testid="property-create-submit" disabled={!p.address} onClick={createProperty} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Add property</button>
        </Modal>
      )}
    </div>
  );
}

const inp = "w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none";
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card-tac w-full max-w-md space-y-3 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="font-display text-lg font-semibold text-white">{title}</h2><button onClick={onClose} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button></div>
        {children}
      </div>
    </div>
  );
}
